import React, { useState } from "react";
import styled from "styled-components";
import {
  Link,
  Redirect,
  RouteComponentProps,
  useLocation
} from "@reach/router";
import { useAuth } from "../context/AuthContext";
import { Formik, Form, FormikErrors } from "formik";
import { signInEmail } from "../api/auth";
import { TextField } from "../components/TextField";
import { TitleHeading } from "../components/Heading";
import { validateEmail } from "./validate-email";
import { Stack } from "../components/Stack";
import { FormElementContainer } from "./FormElementContainer";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormSubmitButton } from "./FormSubmitButton";

const Text = styled.p``;

type FormValues = {
  email: string;
  password: string;
};

const SignIn: React.FC<RouteComponentProps> = () => {
  const location = useLocation();
  const { isSignedIn } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get("return_to");

  const [signInError, setSignInError] = useState<Error | null>(null);

  if (isSignedIn) {
    return <Redirect to={redirect || "/"} noThrow />;
  }

  return (
    <FormElementContainer>
      <TitleHeading>Sign In to President.Game</TitleHeading>
      <Stack data-size="large">
        <Formik<FormValues>
          initialValues={{
            email: "",
            password: ""
          }}
          validate={(values) => {
            const errors: FormikErrors<FormValues> = {};
            if (!validateEmail(values.email)) {
              errors.email = "🤔 That email doesnt look valid. Try again?";
            }
            if (!values.password || values.password.length < 6) {
              errors.password = "Password has to be at least 6 characters!";
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting }) => {
            setSignInError(null);
            try {
              await signInEmail(values.email, values.password);
            } catch (err: any) {
              setSignInError(err);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form noValidate>
              <TextField
                label="Email"
                id="email"
                name="email"
                autoComplete="email"
                placeholder="jane@example.com"
                type="email"
              />

              <TextField
                label="Password"
                id="password"
                name="password"
                autoComplete="current-password"
                placeholder="·············"
                type="password"
              />

              {signInError && (
                <FormErrorMessage>{signInError.message}</FormErrorMessage>
              )}

              <FormSubmitButton type="submit" disabled={isSubmitting}>
                Sign In
              </FormSubmitButton>
            </Form>
          )}
        </Formik>
        <Text>
          Don't have an account yet?
          <FormSubmitButton
            as={Link}
            to={"/sign-up" + (location.search || "")}
            variant="outline"
          >
            Sign Up
          </FormSubmitButton>
        </Text>
      </Stack>
    </FormElementContainer>
  );
};

export default SignIn;
